import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash, faSpinner, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Integration, SynchronizationJob } from '@/types';

const SynchronizationJobs: React.FC = () => {
  const [filters, setFilters] = useState({
    dateCreatedFrom: '',
    dateCreatedTo: '',
    status: '',
    synchronizationType: '',
  });

  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState<{ objects: SynchronizationJob[], total: number, page: number } | null>(null);
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    perPage: 25,
  });
  
  const [sorting, setSorting] = useState({
    sortBy: 'id',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  const [selectedJobDetails, setSelectedJobDetails] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Helper function to convert camelCase to snake_case for API
  const convertToApiParams = (filters: Record<string, string>) => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== '') {
        const apiKey = key === 'dateCreatedFrom' ? 'date_from' :
                      key === 'dateCreatedTo' ? 'date_to' :
                      key === 'synchronizationType' ? 'synchronization_type' :
                      key;
        acc[apiKey] = value;
      }
      return acc;
    }, {} as Record<string, string>);
  };

  // Helper function to refresh data after operations
  const refreshData = async () => {
    if (isSearchActive) {
      const searchParams = convertToApiParams(filters);

      const fullSearchParams = {
        ...searchParams,
        order_by: sorting.sortBy,
        order_type: sorting.sortOrder,
        page: pagination.currentPage,
      };

      try {
        const data = await apiService.searchSynchronizationJobs(fullSearchParams);
        setSearchResults(data);
        
        const newTotalPages = Math.ceil(data.total / pagination.perPage);
        
        // If current page is empty and there are other pages, go to the last page
        if (data.objects.length === 0 && newTotalPages > 0 && pagination.currentPage > 1) {
          const lastPage = newTotalPages;
          setPagination(prev => ({
            ...prev,
            currentPage: lastPage,
            totalPages: newTotalPages,
          }));
          
          // Re-fetch with the last page
          const lastPageParams = { ...fullSearchParams, page: lastPage };
          const lastPageData = await apiService.searchSynchronizationJobs(lastPageParams);
          setSearchResults(lastPageData);
        } else {
          setPagination(prev => ({
            ...prev,
            totalPages: newTotalPages,
          }));
        }
      } catch (error) {
        console.error('Failed to refresh search results:', error);
      }
    } else {
      // Refresh main query data
      queryClient.invalidateQueries('synchronizationJobs');
    }
  };

  // Fetch synchronization jobs
  const { data: jobsData, isLoading } = useQuery(
    ['synchronizationJobs', pagination.currentPage, sorting.sortBy, sorting.sortOrder],
    () => apiService.getSynchronizationJobs({
      order_by: sorting.sortBy,
      order_type: sorting.sortOrder,
      page: pagination.currentPage,
    }),
    {
      onSuccess: (data) => {
        setPagination(prev => ({
          ...prev,
          totalPages: Math.ceil(data.total / prev.perPage),
        }));
      },
    }
  );

  // Fetch sites and integrations for display
  const { data: integrations = [] } = useQuery('integrations', apiService.getIntegrations);

  // Delete mutation
  const deleteMutation = useMutation(apiService.deleteSynchronizationJob, {
    onSuccess: () => {
      refreshData();
    },
    onError: (error) => {
      console.error('Delete failed:', error);
      alert('Failed to delete job');
    },
  });

  // Search mutation
  const searchMutation = useMutation(
    (searchParams: Record<string, any>) => apiService.searchSynchronizationJobs(searchParams),
    {
      onSuccess: (data) => {
        setPagination(prev => ({
          ...prev,
          totalPages: Math.ceil(data.total / prev.perPage),
        }));
        // Store search results in state
        setSearchResults(data);
      },
      onError: (error) => {
        console.error('Search failed:', error);
        alert('Search failed');
      },
    }
  );

  const statusOptions = [
    { value: '', label: 'Select status' },
    { value: 'error', label: 'Error' },
    { value: 'completed', label: 'Completed' },
  ];

  const synchronizationTypes = [
    { value: '', label: 'Select synchronization type' },
    { value: 'product_create', label: 'Create products' },
    { value: 'update_stock', label: 'Update Stock' },
    { value: 'order_create', label: 'Create orders' },
    { value: 'invoice_create', label: 'Create invoices' },
    { value: 'order_update', label: 'Update orders' },
    { value: 'subscription_create', label: 'Create subscriptions' },
    { value: 'refund_create', label: 'Create refunds' },
    { value: 'check_stock', label: 'Check stock' },
    { value: 'order_delivery_state_listen', label: 'Order Delivery State Listener' },
    { value: 'transaction_to_invoice_payments', label: 'Match Payment transaction with invoices' },
    { value: 'update_invoice_payment_status', label: 'Update Invoice payment status' },
    { value: 'payout_to_accumulated_report_voucher', label: 'Create Payment report' },
    { value: 'transaction_to_voucher', label: 'Create transaction vouchers' },
    { value: 'reactivate_activate_products', label: 'Reactivate/Activate products' },
    { value: 'payout_to_daily_sales_and_fees_vouchers', label: 'Book daily sales and fees' },
  ];

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    const searchParams = convertToApiParams(filters);

    // Add pagination and sorting to search params
    const fullSearchParams = {
      ...searchParams,
      order_by: sorting.sortBy,
      order_type: sorting.sortOrder,
      page: pagination.currentPage,
    };

    setIsSearchActive(true);
    searchMutation.mutate(fullSearchParams);
  };

  const handleClearSearch = () => {
    setFilters({
      dateCreatedFrom: '',
      dateCreatedTo: '',
      status: '',
      synchronizationType: '',
    });
    setIsSearchActive(false);
    setSearchResults(null);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    queryClient.invalidateQueries('synchronizationJobs');
  };

  const handleSort = (field: string) => {
    const newOrder = sorting.sortBy === field && sorting.sortOrder === 'desc' ? 'asc' : 'desc';
    setSorting({
      sortBy: field,
      sortOrder: newOrder,
    });
    
    // If search is active, perform new search with updated sorting
    if (isSearchActive) {
      const searchParams = convertToApiParams(filters);

      const fullSearchParams = {
        ...searchParams,
        order_by: field,
        order_type: newOrder,
        page: pagination.currentPage,
      };

      searchMutation.mutate(fullSearchParams);
    }
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page,
    }));
    
    // If search is active, perform new search with updated page
    if (isSearchActive) {
      const searchParams = convertToApiParams(filters);

      const fullSearchParams = {
        ...searchParams,
        order_by: sorting.sortBy,
        order_type: sorting.sortOrder,
        page: page,
      };

      searchMutation.mutate(fullSearchParams);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      deleteMutation.mutate(id);
    }
  };

  const getIntegrationName = (integrationId: number) => {
    const integration = integrations.find((i: Integration) => i.id === integrationId);
    return integration?.name || `Integration ${integrationId}`;
  };

  const getSortIcon = (field: string) => {
    if (sorting.sortBy !== field) {
      return <FontAwesomeIcon icon={faSort} className="w-3 h-3 text-gray-400" />;
    }
    return sorting.sortOrder === 'asc' ? 
      <FontAwesomeIcon icon={faSortUp} className="w-3 h-3 text-orange-500" /> :
      <FontAwesomeIcon icon={faSortDown} className="w-3 h-3 text-orange-500" />;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge badge-success">Completed</span>;
      case 'error':
        return <span className="badge badge-danger">Error</span>;
      case 'running':
        return <span className="badge badge-info">Running</span>;
      default:
        return <span className="badge badge-secondary">{status}</span>;
    }
  };

  // Use search results if available, otherwise use regular query data
  const synchronizationJobs = isSearchActive && searchResults?.objects ? searchResults.objects : (jobsData?.objects || []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Synchronization Jobs</h1>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="form-section">
          <div className="form-row">
            <div className="form-column">
              <div>
                <label className="form-label">From Date:</label>
                <input
                  type="date"
                  value={filters.dateCreatedFrom}
                  onChange={(e) => handleFilterChange('dateCreatedFrom', e.target.value)}
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">To Date:</label>
                <input
                  type="date"
                  value={filters.dateCreatedTo}
                  onChange={(e) => handleFilterChange('dateCreatedTo', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-column">
              <div>
                <label className="form-label">Status:</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="form-input"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Synchronization Type:</label>
                <select
                  value={filters.synchronizationType}
                  onChange={(e) => handleFilterChange('synchronizationType', e.target.value)}
                  className="form-input"
                >
                  {synchronizationTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 space-x-3">
            {isSearchActive && (
              <button
                onClick={handleClearSearch}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                Clear Search
              </button>
            )}
            <button
              onClick={handleSearch}
              disabled={searchMutation.isLoading}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {searchMutation.isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} className="w-4 h-4" />
                  <span>Search</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table min-w-full">
            <thead>
              <tr>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center space-x-1">
                    <span>ID</span>
                    {getSortIcon('id')}
                  </div>
                </th>
                <th>Site ID</th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('synchronization_type')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    {getSortIcon('synchronization_type')}
                  </div>
                </th>
                <th>Master System</th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('slave_system_id')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Slave System</span>
                    {getSortIcon('slave_system_id')}
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {getSortIcon('status')}
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('progress')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Progress</span>
                    {getSortIcon('progress')}
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('start_time')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Start Time</span>
                    {getSortIcon('start_time')}
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('end_time')}
                >
                  <div className="flex items-center space-x-1">
                    <span>End Time</span>
                    {getSortIcon('end_time')}
                  </div>
                </th>
                <th>Since Last Updated</th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('details')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Details</span>
                    {getSortIcon('details')}
                  </div>
                </th>
                <th className="text-right sticky right-0 bg-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {synchronizationJobs.map((job: SynchronizationJob) => (
                <tr key={job.id}>
                  <td className="font-mono text-sm">{job.id}</td>
                  <td>{job.site_id}</td>
                  <td>
                    <span className="badge badge-secondary">
                      {job.synchronization_type}
                    </span>
                  </td>
                  <td>{getIntegrationName(job.master_system_id)}</td>
                  <td>{getIntegrationName(job.slave_system_id)}</td>
                  <td>{getStatusBadge(job.status || 'unknown')}</td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${job.progress || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">{job.progress || 0}%</span>
                    </div>
                  </td>
                  <td className="text-sm text-gray-600">
                    {formatDate(job.start_time || '')}
                  </td>
                  <td className="text-sm text-gray-600">
                    {formatDate(job.end_time || '')}
                  </td>
                  <td>
                    <span className={`badge ${job.since_last_updated ? 'badge-success' : 'badge-secondary'}`}>
                      {job.since_last_updated ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="max-w-xs truncate text-sm text-gray-600">
                    {job.details ? (
                      <button
                        onClick={() => setSelectedJobDetails(job.details || '')}
                        className="text-gray-600 hover:text-gray-800 underline cursor-pointer truncate block w-full text-left"
                        title="Click to view full details"
                      >
                        {job.details}
                      </button>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td className="text-right sticky right-0 bg-white px-4">
                    <button
                      onClick={() => handleDelete(job.id)}
                      className="btn-icon btn-icon-danger"
                      title="Delete"
                      disabled={deleteMutation.isLoading}
                    >
                      {deleteMutation.isLoading ? (
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Page {pagination.currentPage} of {pagination.totalPages}
            {isSearchActive && searchResults && (
              <span className="ml-2 text-orange-600">
                ({searchResults.total} total results)
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 rounded ${
                      page === pagination.currentPage
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= pagination.totalPages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {selectedJobDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl max-h-[80vh] w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Job Details</h3>
              <button
                onClick={() => setSelectedJobDetails(null)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>
            <div className="bg-gray-50 p-4 rounded border max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                {selectedJobDetails}
              </pre>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setSelectedJobDetails(null)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SynchronizationJobs;
